import { FC } from "react";
import { NavLink } from "react-router-dom";
import { Home, FileText, User, Unplug } from "lucide-react";
import logo from "@/assets/icons/logo.svg";
import { cn } from "@/lib/utils";
import ROUTES from "@/data/Routes";
import { ROLES } from "@/data/interfaces/RolesEnum";
import { useAuth } from "@/data/context/authContext";

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href: string;
  roles?: ROLES[];
}

const navItems: NavItem[] = [
  {
    id: "dashboard",
    label: "Your Dashboard",
    icon: <Home className="size-5" />,
    href: ROUTES.DASHBOARD,
    roles: [ROLES.SUPER_ADMIN],
  },
  {
    id: "results",
    label: "Results",
    icon: <FileText className="size-5" />,
    href: ROUTES.RESULTS,
    roles: [ROLES.SUPER_ADMIN],
  },
  {
    id: "profile",
    label: "Profile",
    icon: <User className="size-5" />,
    href: ROUTES.ACCOUNT,
  },
  {
    id: "connectivity",
    label: "Connectivity",
    icon: <Unplug className="size-5" />,
    href: ROUTES.CONNECTIVITY,
    roles: [ROLES.SUPER_ADMIN],
  },
];

const DashboardSidebar: FC = () => {
  const { user } = useAuth();

  const visibleNavItems = navItems.filter((item) => {
    if (!user) return false;
    if (!item.roles?.length) return true;
    return item.roles.includes(user.role);
  });

  return (
    <aside className="flex h-full min-h-screen w-73 flex-col border-r border-gray-200 bg-white md:min-h-full">
      {/* Logo */}
      <div className="p-6">
        <NavLink to="/">
          <img src={logo} className="w-[167px]" alt="Telomera logo" />
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {visibleNavItems.map((item) => (
            <li key={item.id}>
              <NavLink
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-lg px-4 py-3 transition-colors",
                    "text-ink [&_svg]:text-ink hover:bg-[#f5f0ff]",
                    isActive &&
                      "bg-[#ede6f7] font-medium text-ink [&_svg]:text-ink",
                  )
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

export default DashboardSidebar;
